import React, {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import ToastContext from "./ToastContext";
import { ToastContainer } from "react-bootstrap";
import "./toast.css";
import userService from "../../Services/userService";
import UserContext from "../UserContext/UserContext";

import { useNavigate } from "react-router-dom";
import SubjectsContext from "../SubjectsContext/SubjectsContext";
import ToastItem from "./ToastItem";
import CriticalAlertDialog from "./CriticalAlertDialog";
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary";
import { useAnnounce } from "../../Components/LiveAnnouncer/LiveAnnouncer";
import { isSystemUseNotificationRequired } from "../../Components/SystemUseNotification/systemUseGate";
import {
  CRITICAL_WAITING_MESSAGE,
  alertActionErrorMessage,
  countLabel,
  criticalAlertName,
  describeAlertRemoval,
  describeCriticalAlert,
  getAlertActionLabel,
} from "./toastPresentation";

export const SLOW_ACTION_MS = 15000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const { userData, accessToken, imageSasToken, userPermissions } =
    useContext(UserContext);
  const [acknowledgedUserId, setAcknowledgedUserId] = useState(null);
  const sessionUserId = userData?.userInfo?.id || null;
  const hasAcknowledgedSystemUse =
    !!sessionUserId && acknowledgedUserId === sessionUserId;
  const setIsSystemUseAcknowledged = useCallback(
    (acknowledged) =>
      setAcknowledgedUserId(acknowledged ? sessionUserId : null),
    [sessionUserId],
  );
  const isSystemUseAcknowledged =
    hasAcknowledgedSystemUse ||
    !isSystemUseNotificationRequired(userData, userPermissions);
  const [actionStates, setActionStates] = useState({});
  const [criticalLayer, setCriticalLayer] = useState(null);
  const [isDialogHealthy, setIsDialogHealthy] = useState(false);
  const inFlightRef = useRef(new Map());
  const attemptRef = useRef(0);
  const resolvedRef = useRef(new Map());
  const dismissedRef = useRef(new Set());
  const seenCriticalIdsRef = useRef(new Set());
  const noticeArrivalIdsRef = useRef(new Set());
  const previousCriticalRef = useRef({ toasts: [], dialogOpen: false });
  const previousReopenCountRef = useRef(0);
  const { subjects } = useContext(SubjectsContext);
  const navigate = useNavigate();
  const announce = useAnnounce();

  const addToast = useCallback(
    (
      title,
      category = "default",
      variant = "primary",
      documentInfo = null,
      setIsCriticalSoundPlaying = null,
      oldDocumentInfo = null,
      currentSound = null,
    ) => {
      setToasts((prevState) => [
        ...prevState,
        {
          id: `${Date.now()}-${Math.random()}`,
          title,
          category,
          variant,
          documentInfo,
          setIsCriticalSoundPlaying,
          oldDocumentInfo,
          currentSound,
        },
      ]);
    },
    [],
  );

  const removeToast = useCallback((id) => {
    setToasts((prevState) => {
      const toastToRemove = prevState.find((toast) => toast.id === id);
      const updatedToasts = prevState.filter((toast) => toast.id !== id);

      const wasCritical = toastToRemove?.category === "Critical";

      const remainingCriticalToasts = updatedToasts.filter(
        (t) => t.category === "Critical" && t.currentSound,
      );

      if (wasCritical && remainingCriticalToasts.length === 0) {
        if (toastToRemove.setIsCriticalSoundPlaying !== null) {
          toastToRemove.setIsCriticalSoundPlaying(false);
        }
        toastToRemove.currentSound?.stop();
      }

      return updatedToasts;
    });
  }, []);

  const runAlertAction = useCallback(
    (action, subject, toastId, payload, failureLabel) => {
      const key = `${subject?.id}:${subject?.alertId}`;
      const running = inFlightRef.current.get(key);
      if (running && !running.slow) return;
      attemptRef.current += 1;
      const attempt = { token: attemptRef.current, slow: false };
      inFlightRef.current.set(key, attempt);

      const isCurrent = (state) => state?.token === attempt.token;
      setActionStates((prev) => ({
        ...prev,
        [toastId]: { status: "pending", action, token: attempt.token },
      }));

      const slowTimer = setTimeout(() => {
        if (inFlightRef.current.get(key) !== attempt) return;
        attempt.slow = true;
        setActionStates((prev) =>
          isCurrent(prev[toastId])
            ? { ...prev, [toastId]: { ...prev[toastId], status: "slow" } }
            : prev,
        );
      }, SLOW_ACTION_MS);

      userService
        .logUsersAlertAction(payload, accessToken)
        .then(() => {
          resolvedRef.current.set(toastId, action);
          removeToast(toastId);
          setActionStates((prev) => {
            if (!isCurrent(prev[toastId])) return prev;
            const next = { ...prev };
            delete next[toastId];
            return next;
          });
        })
        .catch((error) => {
          console.error(failureLabel, error);
          const message = alertActionErrorMessage(
            error,
            getAlertActionLabel(action, userData),
          );
          setActionStates((prev) =>
            isCurrent(prev[toastId])
              ? {
                  ...prev,
                  [toastId]: {
                    status: "error",
                    action,
                    token: attempt.token,
                    message,
                  },
                }
              : prev,
          );
        })
        .finally(() => {
          clearTimeout(slowTimer);
          if (inFlightRef.current.get(key) === attempt) {
            inFlightRef.current.delete(key);
          }
        });
    },
    [accessToken, removeToast, userData],
  );

  const respondingButton = useCallback(
    (subject, index) => {
      const payload = {
        entityId: userData?.userInfo?.entityId,
        subjectId: subject.id,
        action: "Responded",
        alertId: subject.alertId,
      };

      if (userData?.userInfo?.firstName) {
        payload.firstName = userData.userInfo.firstName;
      }

      if (userData?.userInfo?.lastName) {
        payload.lastName = userData.userInfo.lastName;
      }

      if (userData?.userInfo?.title) {
        payload.title = userData.userInfo.title;
      }

      runAlertAction(
        "Responded",
        subject,
        index,
        payload,
        "Failed to log responding action:",
      );
    },
    [
      userData?.userInfo?.entityId,
      userData?.userInfo?.firstName,
      userData?.userInfo?.lastName,
      userData?.userInfo?.title,
      runAlertAction,
    ],
  );

  useEffect(() => {
    const currentUserId = userData?.userInfo?.id;

    setToasts((prevToasts) => {
      const removed = [];

      const updatedToasts = prevToasts.filter((toast) => {
        if (toast.category !== "Critical") return true;

        const subject = subjects?.find((s) => s.id === toast.documentInfo?.id);
        if (!subject || subject.wellnessCategory !== "High") {
          removed.push(toast);
          return false;
        }

        const userHasResponded = subject?.officersRespondedToCritical?.some(
          (officer) => officer.user === currentUserId,
        );
        if (userHasResponded) {
          removed.push(toast);
          return false;
        }

        return true;
      });

      if (removed.length === 0) return prevToasts;

      const remainingCriticalToasts = updatedToasts.filter(
        (t) => t.category === "Critical",
      );

      if (remainingCriticalToasts.length === 0) {
        removed.forEach((toast) => toast.currentSound?.stop());
      }

      return updatedToasts;
    });
  }, [subjects, userData]);

  const busyButton = useCallback(
    (subject, index) => {
      const payload = {
        entityId: userData?.userInfo?.entityId,
        subjectId: subject.id,
        action: "Busy",
        alertId: subject.alertId,
      };
      runAlertAction(
        "Busy",
        subject,
        index,
        payload,
        "Failed to log busy action:",
      );
    },
    [userData?.userInfo?.entityId, runAlertAction],
  );

  const dismissBrokenAlert = useCallback(
    (id) => {
      dismissedRef.current.add(id);
      removeToast(id);
    },
    [removeToast],
  );

  const criticalToasts = useMemo(
    () => toasts.filter((toast) => toast.category === "Critical"),
    [toasts],
  );
  const statusToasts = useMemo(
    () => toasts.filter((toast) => toast.category !== "Critical"),
    [toasts],
  );
  const criticalIdsKey = criticalToasts.map((toast) => toast.id).join("|");

  useEffect(() => {
    const ids = new Set(toasts.map((toast) => toast.id));
    setActionStates((prev) => {
      const stale = Object.keys(prev).filter((id) => !ids.has(id));
      if (!stale.length) return prev;
      const next = { ...prev };
      stale.forEach((id) => delete next[id]);
      return next;
    });
  }, [toasts]);

  const popupsDisabled = !!userData?.userInfo?.disableAlertPopups;
  const isCriticalSurfaceVisible = isSystemUseAcknowledged && !popupsDisabled;
  const isCriticalDialogShown =
    isCriticalSurfaceVisible && criticalToasts.length > 0;
  const isCriticalDialogOpen = isCriticalDialogShown && isDialogHealthy;
  const hiddenCriticalCount =
    isSystemUseAcknowledged && popupsDisabled ? criticalToasts.length : 0;

  const hasCriticalToast = criticalToasts.length > 0;
  const hasActiveCriticalSubject =
    subjects?.some((subject) => subject?.wellnessCategory === "High") ?? false;
  const hasCriticalAlert = hasCriticalToast || hasActiveCriticalSubject;

  const contextValue = useMemo(
    () => ({
      addToast,
      removeToast,
      setIsSystemUseAcknowledged,
      isSystemUseAcknowledged,
      hasCriticalAlert,
      isCriticalDialogOpen,
      isCriticalOverlayShown: isCriticalDialogShown,
      hiddenCriticalCount,
      criticalAlertCount: criticalToasts.length,
    }),
    [
      addToast,
      removeToast,
      setIsSystemUseAcknowledged,
      isSystemUseAcknowledged,
      hasCriticalAlert,
      isCriticalDialogOpen,
      isCriticalDialogShown,
      hiddenCriticalCount,
      criticalToasts.length,
    ],
  );

  const onDeviceToastCloseClicked = useCallback((e, id) => {
    e.stopPropagation();

    setToasts((prevToasts) => {
      const toastBeingClosed = prevToasts.find((toast) => toast.id === id);

      const updatedToasts = prevToasts.filter((toast) => toast.id !== id);

      if (toastBeingClosed && toastBeingClosed.category !== "Critical") {
        const nonCriticalRemaining = updatedToasts.filter(
          (t) =>
            t.category !== "Critical" &&
            t.documentInfo.priorityValue !== 36 &&
            t.documentInfo.priorityValue !== 195,
        );

        if (nonCriticalRemaining.length === 0) {
          toastBeingClosed.currentSound?.stop();
        }
      }

      return updatedToasts;
    });
  }, []);

  const subjectsToReopen = useMemo(() => {
    return subjects?.filter((subject) => {
      const officers = subject?.officersRespondedToCritical || [];
      const hasUserResponded = officers?.some(
        (officer) => officer?.user === userData?.userInfo?.id,
      );
      return subject?.wellnessCategory === "High" && !hasUserResponded;
    });
  }, [subjects, userData]);

  const reopenCriticalAlerts = useCallback(() => {
    if (subjectsToReopen?.length) {
      const queued = new Set(
        criticalToasts.map((toast) => toast.documentInfo?.id),
      );
      subjectsToReopen.forEach((subject) => {
        if (queued.has(subject?.id)) return;
        addToast("Critical Alert", "Critical", "danger", subject);
      });
    }
  }, [subjectsToReopen, addToast, criticalToasts]);

  const isBubbleVisible =
    !popupsDisabled &&
    subjectsToReopen?.length > 0 &&
    userData?.userInfo?.userRole !== "4SightAdmin" &&
    isSystemUseAcknowledged;
  const reopenCount = subjectsToReopen?.length || 0;
  const visibleReopenCount = isBubbleVisible ? reopenCount : 0;

  useEffect(() => {
    const seen = seenCriticalIdsRef.current;
    const arrivals = criticalToasts.filter((toast) => !seen.has(toast.id));
    const current = new Set(criticalToasts.map((toast) => toast.id));
    [...seen].forEach((id) => {
      if (!current.has(id)) seen.delete(id);
    });
    [...noticeArrivalIdsRef.current].forEach((id) => {
      if (!current.has(id)) noticeArrivalIdsRef.current.delete(id);
    });
    arrivals.forEach((toast) => seen.add(toast.id));
    if (!arrivals.length) return;
    if (!isSystemUseAcknowledged) {
      arrivals.forEach((toast) => noticeArrivalIdsRef.current.add(toast.id));
      announce(CRITICAL_WAITING_MESSAGE, { politeness: "assertive" });
      return;
    }
    if (popupsDisabled) {
      arrivals.forEach((toast) =>
        announce(
          `Wellness Check Required: ${describeCriticalAlert(toast, userData)} Pop-up alerts are off.`,
          { politeness: "assertive" },
        ),
      );
    }
  }, [criticalToasts]);

  useEffect(() => {
    if (!isSystemUseAcknowledged) return;
    const queued = criticalToasts.filter((toast) =>
      noticeArrivalIdsRef.current.has(toast.id),
    );
    noticeArrivalIdsRef.current.clear();
    if (!queued.length || !popupsDisabled) return;
    const names = queued.map((toast) => criticalAlertName(toast)).join(", ");
    announce(
      `${countLabel(queued.length, "Wellness Check Required", "Wellness Checks Required")} arrived while the system use notice was open: ${names}.`,
      { politeness: "assertive" },
    );
  }, [isSystemUseAcknowledged]);

  useEffect(() => {
    const previous = previousCriticalRef.current;
    previousCriticalRef.current = {
      toasts: criticalToasts,
      dialogOpen: isCriticalDialogShown,
    };
    if (!previous.dialogOpen || criticalToasts.length > 0) return;
    const removed = previous.toasts;
    if (!removed.length) return;
    const respondedSubjects = new Set(
      removed
        .filter((toast) => resolvedRef.current.get(toast.id) === "Responded")
        .map((toast) => toast.documentInfo?.id),
    );
    const message =
      removed.length === 1
        ? describeAlertRemoval(removed[0], {
            resolvedAction: resolvedRef.current.get(removed[0].id),
            dismissed: dismissedRef.current.has(removed[0].id),
            subjects,
            userId: userData?.userInfo?.id,
            actionState: actionStates[removed[0].id],
            userData,
          })
        : `${countLabel(removed.length, "Wellness Check Required", "Wellness Checks Required")} cleared.`;
    const waiting = isBubbleVisible
      ? subjectsToReopen.filter(
          (subject) => !respondedSubjects.has(subject?.id),
        ).length
      : 0;
    announce(
      waiting > 0
        ? `${message} ${countLabel(waiting, "Wellness Check Required", "Wellness Checks Required")} still awaiting your response.`
        : message,
    );
  }, [criticalToasts]);

  useEffect(() => {
    const previous = previousReopenCountRef.current;
    previousReopenCountRef.current = visibleReopenCount;
    if (isCriticalDialogShown || visibleReopenCount <= previous) return;
    announce(
      `${countLabel(visibleReopenCount, "Wellness Check Required", "Wellness Checks Required")} awaiting your response.`,
    );
  }, [visibleReopenCount]);

  useEffect(() => {
    const ids = new Set(toasts.map((toast) => toast.id));
    [resolvedRef.current, dismissedRef.current].forEach((store) => {
      [...store.keys()].forEach((id) => {
        if (!ids.has(id)) store.delete(id);
      });
    });
  }, [toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {hasCriticalToast && (
        <div
          className="blur-overlay"
          style={{
            display: isCriticalSurfaceVisible ? null : "none",
          }}
        ></div>
      )}
      <ToastContainer
        position="top-end"
        style={{
          marginTop: 85,
          marginRight: 12,
          display: isSystemUseAcknowledged ? null : "none",
        }}
      >
        {statusToasts.map((toast) => (
          <ErrorBoundary
            key={toast.id}
            variant="item"
            label="An alert"
            onDismiss={() => removeToast(toast.id)}
          >
            <ToastItem
              toast={toast}
              userData={userData}
              imageSasToken={imageSasToken}
              removeToast={removeToast}
              onClose={onDeviceToastCloseClicked}
              navigate={navigate}
            />
          </ErrorBoundary>
        ))}
      </ToastContainer>
      <div className="critical-alert-layer" ref={setCriticalLayer} />
      <ErrorBoundary
        variant="section"
        className="critical-alert-dialog-fallback"
        resetKey={criticalIdsKey}
      >
        {isCriticalDialogShown && criticalLayer && (
          <CriticalAlertDialog
            alerts={criticalToasts}
            userData={userData}
            subjects={subjects}
            actionStates={actionStates}
            resolvedRef={resolvedRef}
            dismissedRef={dismissedRef}
            onRespond={respondingButton}
            onOccupied={busyButton}
            onDismissBroken={dismissBrokenAlert}
            container={criticalLayer}
            onHealthyChange={setIsDialogHealthy}
            noticeArrivalCount={
              criticalToasts.filter((toast) =>
                noticeArrivalIdsRef.current.has(toast.id),
              ).length
            }
          />
        )}
      </ErrorBoundary>
      {isBubbleVisible && (
        <button
          type="button"
          className="toast-critical-realert-container"
          aria-label={`${countLabel(reopenCount, "Wellness Check Required", "Wellness Checks Required")} awaiting your response`}
          onClick={reopenCriticalAlerts}
        >
          <span
            className="toast-critical-realert-circle-container"
            aria-hidden="true"
          >
            {reopenCount}
          </span>
        </button>
      )}
    </ToastContext.Provider>
  );
};
